import { usePageTitle } from "@/hooks/usePageTitle";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import api from "@/lib/api";
import { toast } from "sonner";
import { Check, Loader2, Clock, Sparkles, ArrowRight } from "lucide-react";

const GOLD = "#C9A227";

const OFFERS = [
  {
    plan: "metabolic_promo",
    name: "Metabolic Health 1-1",
    price: 199,
    was: 249,
    tagline: "Full health and lifestyle assessment with food diary analysis. Advice provided aligned with the 4 pillars for metabolic health",
    includes: [
      "1 × 60-minute health assessment and food diary analysis with initial recommendations for improvement",
      "2 x 30 minute reviews to monitor progress, address challenges and make adjustments where needed",
      "Personal guidance from Andrea Wood, Nutritional Therapist with 17 years' experience",
    ],
  },
  {
    plan: "food_diary_promo",
    name: "Food Diary Analysis 1-1",
    price: 75,
    was: 99,
    tagline: "Find out exactly what to change — and how",
    includes: [
      "Food diary nutritional analysis",
      "A 60-minute review of your current diet",
      "Advice on the nutritional modifications needed to align with the 4 Pillars — and how to implement them",
    ],
  },
];

function Countdown({ deadline }) {
  const [left, setLeft] = useState(() => new Date(deadline) - new Date());
  useEffect(() => {
    const t = setInterval(() => setLeft(new Date(deadline) - new Date()), 1000);
    return () => clearInterval(t);
  }, [deadline]);
  if (left <= 0) return null;
  const d = Math.floor(left / 86400000);
  const h = Math.floor((left % 86400000) / 3600000);
  const m = Math.floor((left % 3600000) / 60000);
  const s = Math.floor((left % 60000) / 1000);
  const Cell = ({ v, l }) => (
    <div className="bg-white/10 rounded-xl px-4 py-3 min-w-[70px] text-center">
      <div className="font-heading text-3xl text-white">{String(v).padStart(2, "0")}</div>
      <div className="text-[10px] uppercase tracking-widest text-white/60 mt-1">{l}</div>
    </div>
  );
  return (
    <div className="flex gap-3 flex-wrap" data-testid="promo-countdown">
      <Cell v={d} l="days" /><Cell v={h} l="hours" /><Cell v={m} l="mins" /><Cell v={s} l="secs" />
    </div>
  );
}

function OfferCard({ offer, featured }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [terms, setTerms] = useState(false);
  const [busy, setBusy] = useState(false);
  const buy = async () => {
    if (!name.trim()) { toast.error("Please enter your name."); return; }
    if (!email.trim()) { toast.error("Please enter your email."); return; }
    if (!terms) { toast.error("Please accept the Terms & Conditions to continue."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        plan: offer.plan,
        origin_url: window.location.origin,
        email: email.trim(),
        payer_name: name.trim(),
        terms_accepted: terms,
      });
      window.location.href = data.url;
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" && d.length < 160 ? d : "Could not start checkout. Please try again shortly.");
      setBusy(false);
    }
  };
  return (
    <div className={`bg-white rounded-2xl p-8 shadow-sm flex flex-col ${featured ? "border-2" : "border border-[#E7E5E4]"}`}
      style={featured ? { borderColor: GOLD } : undefined} data-testid={`offer-card-${offer.plan}`}>
      {featured && (
        <span className="self-start text-[10px] font-bold uppercase tracking-[0.2em] rounded-full px-3 py-1 mb-4"
          style={{ background: "rgba(201,162,39,0.12)", color: "#8a6d12" }}>Most comprehensive</span>
      )}
      <h2 className="font-heading text-2xl text-navy">{offer.name}</h2>
      <p className="mt-1 text-sm text-[#78716C]">{offer.tagline}</p>
      <div className="mt-5 flex items-end gap-3">
        <span className="font-heading text-5xl text-navy" data-testid={`offer-price-${offer.plan}`}>£{offer.price}</span>
        <span className="text-xl text-[#A8A29E] line-through mb-1">£{offer.was}</span>
        <span className="text-xs font-semibold text-white rounded-full px-2.5 py-1 mb-2" style={{ background: GOLD }}>
          Save £{offer.was - offer.price}
        </span>
      </div>
      <ul className="mt-6 space-y-3 flex-1">
        {offer.includes.map((inc, i) => (
          <li key={i} className="flex items-start gap-3 text-sm text-[#44403C] leading-relaxed">
            <Check className="w-4 h-4 mt-0.5 shrink-0" style={{ color: GOLD }} /> {inc}
          </li>
        ))}
      </ul>
      <div className="mt-7 space-y-3">
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name"
          data-testid={`offer-name-${offer.plan}`}
          className="w-full border border-[#E7E5E4] rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal/20 focus:border-teal" />
        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Your email"
          data-testid={`offer-email-${offer.plan}`}
          className="w-full border border-[#E7E5E4] rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal/20 focus:border-teal" />
        <label className="flex items-start gap-2.5 text-xs text-[#78716C] cursor-pointer">
          <input type="checkbox" checked={terms} onChange={(e) => setTerms(e.target.checked)}
            data-testid={`offer-terms-${offer.plan}`} className="mt-0.5 accent-teal" />
          <span>I agree to the <Link to="/terms" target="_blank" className="underline text-navy">Terms &amp; Conditions</Link></span>
        </label>
        <button onClick={buy} disabled={busy} data-testid={`offer-buy-${offer.plan}`}
          className="w-full inline-flex items-center justify-center gap-2 rounded-full py-3.5 text-sm font-semibold text-navy hover:opacity-90 transition-opacity disabled:opacity-60"
          style={{ background: GOLD }}>
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
          Secure my place — £{offer.price}
        </button>
        <p className="text-[11px] text-center text-[#A8A29E]">Secure card payment via Stripe · Andrea will contact you within 1–2 days to schedule</p>
      </div>
    </div>
  );
}

export default function Promo() {
  usePageTitle("Exclusive Offers — My Best Body Club");
  const [status, setStatus] = useState(null);
  useEffect(() => {
    api.get("/promo/status").then(({ data }) => setStatus(data)).catch(() => setStatus({ active: false }));
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="max-w-5xl mx-auto px-6 lg:px-8 pt-28 pb-24" data-testid="promo-page">
        {status === null ? (
          <div className="py-32 flex justify-center"><Loader2 className="w-7 h-7 animate-spin text-teal" /></div>
        ) : !status.active ? (
          <div className="bg-navy rounded-2xl p-10 sm:p-14 text-white text-center relative overflow-hidden" data-testid="promo-expired">
            <div className="brand-blob" style={{ width: 280, height: 280, top: -80, right: -60, background: GOLD, opacity: 0.2 }} />
            <Clock className="w-10 h-10 mx-auto" style={{ color: GOLD }} />
            <h1 className="mt-4 font-heading text-3xl sm:text-4xl">This offer has now closed</h1>
            <p className="mt-4 text-white/75 max-w-xl mx-auto">
              These exclusive packages expired on Friday 23rd October at 9pm. Keep an eye out — Andrea runs special offers from time to time.
            </p>
            <Link to="/" className="mt-8 inline-flex items-center gap-2 rounded-full px-7 py-3 text-sm font-semibold text-navy" style={{ background: GOLD }}>
              Explore My Best Body Club <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        ) : (
          <>
            <div className="bg-navy rounded-2xl p-8 sm:p-12 text-white relative overflow-hidden">
              <div className="brand-blob" style={{ width: 320, height: 320, top: -90, right: -70, background: GOLD, opacity: 0.25 }} />
              <div className="relative">
                <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-semibold" style={{ color: GOLD }}>
                  <Sparkles className="w-4 h-4" /> Exclusive event offers
                </span>
                <h1 className="mt-3 font-heading text-4xl sm:text-5xl tracking-tight">Tonight's Special Offers</h1>
                <p className="mt-4 text-lg text-white/85 max-w-2xl">
                  Two exclusive 1-1 packages with Andrea Wood, Nutritional Therapist — at special event prices.
                  Available only until <strong className="text-white">Friday 23rd October, 9pm</strong>.
                </p>
                <div className="mt-7">
                  <Countdown deadline={status.deadline} />
                </div>
              </div>
            </div>
            <div className="mt-10 grid md:grid-cols-2 gap-7 items-stretch">
              <OfferCard offer={OFFERS[0]} featured />
              <OfferCard offer={OFFERS[1]} />
            </div>
            <p className="mt-10 text-center text-sm text-[#78716C] max-w-2xl mx-auto">
              Both packages are delivered personally by Andrea, 1-1 — online or by phone, at times that suit you.
              Once you've paid, Andrea will be in touch within 1–2 days to get your first session booked.
            </p>
          </>
        )}
      </div>
      <Footer />
    </div>
  );
}
